// Internal modules
import { appStore } from '@/store';
import { ERROR_PAYLOAD, logger } from '@/lib/logger';
import { features } from '@/lib/features';
import {
  fallback,
  fallbackToHermesFIContingency,
  focusOnFirstError,
  isBillingAgreementWithoutPurchaseFlow,
  redirectToGenericError,
} from '@/lib/utils';
import { getContent } from '@/lib/content';
import { getAccessToken, getGlobals } from '@/lib/globals';
import { ADDRESS_VALIDATION_ERROR, CPA_AUTH_DECLINED_ERROR } from './constants';
import { FIELD_LEVEL_ERRORS } from './mappings/preAccountCreationErrors';
import type { errorType } from './types';
import { CI } from '../customerInteractions';
import {
  atomics,
  atomicsDomain,
  atomicsErrorReason,
  atomicsStatus,
} from '@/lib/atomics';
import { isNetworkError } from './utils';

const fallbackToHermesOrXoon = ({
  message,
  error,
}: {
  message: string;
  error?: string;
}): void => {
  const isSignUpToken = getAccessToken().type === 'signup';
  let reason = message;

  // if we redirect back to xoon we want to track the exact reason
  if (!isSignUpToken) {
    reason = `${message}_${error}`;
  }

  fallback({
    app: isSignUpToken ? 'hermes' : 'xoon',
    reason,
  });
};

const handle3DSError = ({ message }: { message: string }): void => {
  const parentInteraction = CI.getParent(
    CI.Event.CONTINGENCY_RESOLVE_3DS_USER_INPUT,
  );
  CI.endWith(parentInteraction, CI.Status.FCI);

  // Custom error we throw for 3DS failures
  fallback({
    additionalParams: {
      redirectToHermesOn3DSDecline: 'weasley',
    },
    app: 'hermes',
    reason: message,
  });
};

const handleDefaultError = ({
  errorData,
  message = '',
}: { errorData?: any; message?: string } = {}): void => {
  const error = message.toLowerCase().replace(/\s/g, '_');
  const errorDataCode = errorData?.[0]?.code;

  if (isNetworkError(error)) {
    logger.error(`generic_error_redirect_${error}`, {
      error,
      errorDataCode,
    });
    logger.fpti('generic_error_redirect', {
      ...ERROR_PAYLOAD({
        code: errorDataCode,
        desc: error,
        source: 'handleDefaultError',
      }),
      transition_name: `generic_error_redirect_${error}`,
    });
    redirectToGenericError({ message: 'RETRY_WITHOUT_MERCHANT_FALLBACK' });
    return;
  } else if (error.startsWith('network_error:')) {
    logger.warn(`unhandled_network_error_${error}`, {
      error,
      errorDataCode,
    });
  }

  logger.error(`unhandled_contingency_${error}`, {
    error,
    errorDataCode,
  });

  // TODO: once we've mapped all the errors and ramped we should remove this
  // or change it to a generic error page so we don't have to fallback
  fallbackToHermesOrXoon({ error, message: 'unhandledContingency' });
};

// Some errors are fallbacks for signup but are page level errors for guest
const handlePostAccountCreationError = ({
  contentKey,
  error,
  message,
}: {
  contentKey: string;
  error: errorType;
  message: string;
}): void => {
  const {
    form: {
      onboardOption: { value: onboardOption },
    },
  } = appStore.getState();

  const isGuest = onboardOption === 'guest';
  const isWithoutPurchase = isBillingAgreementWithoutPurchaseFlow(
    getGlobals()?.initialGqlData?.checkoutSession?.checkoutSessionType,
  );

  if (isGuest) {
    // Show page level error for guest account
    return handlePageLevelError({ contentKey, error });
  } else if (isWithoutPurchase) {
    // Guest accounts are not supported for billing without purchase, so all
    // post-account creation error fallbacks for this case go to the member app
    fallbackToHermesFIContingency({ message });
  }

  fallbackToHermesOrXoon({
    error: 'handlePostAccountCreationError_generic',
    message,
  });
};

const handleAddressValidationError = ({ data = [] }: errorType) => {
  const { form: formState } = appStore.getState();
  const { form: formActions, ui: uiActions } = appStore.getActions();

  /**
   * If the user has selected to ship to their billing address then
   * we can just iterate over the billing address fields.
   *
   * - `data` looks like: [
   *    {
   *      field: 'city',
   *      code: 'INVALID_CITY',
   *      argumentsPath: ['billingAddress', 'city']
   *    },
   *    ...
   *  ]
   * - Using a Set to get the unique values since there will be an object in
   *  `data` for each of the three fields associated with the validation error
   *  and therefore we would end up with each address type being included in
   *  the array three times. e.g. ['shippingAddress', 'shippingAddress', etc]
   */
  /**
   * If we have the Residential Address, use the Set to define errors
   */
  let addressesToValidate = [];

  if (
    !formState.shipToBilling.value ||
    features.isActive('hasPrimaryResidentialAddress')
  ) {
    // @ts-expect-error since we are defaulting data to an empty array,
    // ts doesnt like the check
    addressesToValidate = data.map(obj => obj?.argumentsPath?.split('.')[0]);
  } else {
    addressesToValidate = ['billingAddress'];
  }

  const addressTypes = new Set(addressesToValidate);
  const { CLIENT_ERROR_CODE, FIELDS } = ADDRESS_VALIDATION_ERROR;

  // Update the validation error for the necessary address types
  addressTypes.forEach(addressType => {
    formActions.updateAddressValidationError({
      action: 'setError',
      addressType,
      errorCode: CLIENT_ERROR_CODE,
      fields: FIELDS,
    });
  });

  // If our only error is Residential Address
  // we need to Interstitial for the user to fix the error
  if (addressTypes.size === 1 && addressTypes.has('residentialAddress')) {
    uiActions.toggleIsResidentialAddressOpen(true);
  }
};

const handlePageLevelError = ({
  contentKey,
  error,
}: {
  contentKey: string;
  error: errorType;
}): void => {
  const { checkpoints, message } = error;
  const { ui: uiState } = appStore.getState();
  atomics.markProblem({
    context: message,
    domain: atomicsDomain,
    reason: atomicsErrorReason.user,
    status: atomicsStatus.error,
  });

  if (uiState.pageLevelError === contentKey) {
    return logger.info('existing_page_level_error', {
      error: message,
    });
  }

  /**
   * If the error is an address validation error we highlight
   * the three address fields that the error pertains to.
   *
   * Checking the checkpoints here as well as the error code since
   * the error code that is returned in pretty generic, VALIDATION_ERROR,
   * and could potentially be returned for other errors as well as the
   * address validation error.
   */
  if (
    message === ADDRESS_VALIDATION_ERROR.API_ERROR_CODE &&
    checkpoints?.includes(ADDRESS_VALIDATION_ERROR.API_CHECKPOINT)
  ) {
    handleAddressValidationError(error);
  }

  const { ui: uiActions } = appStore.getActions();
  if (message.includes(CPA_AUTH_DECLINED_ERROR.API_ERROR_CODE)) {
    logger.info('setting_decline_notification', { error: message });
    uiActions.setPageWarningMessage(contentKey);
  } else {
    logger.info('setting_page_level_error', { error: message });
    uiActions.setPageLevelError(contentKey);
  }

  uiActions.toggleIsPageLoading(false);

  window.scrollTo(0, 0);
};

const handleNamesValidationError = (error: errorType): void => {
  if (error?.errorData?.length) {
    const actions = appStore.getActions();
    const filteredErrors = error.errorData.filter((f: any) => f);
    filteredErrors.forEach(({ code }: { code: any }) => {
      // @ts-expect-error the code should be limited to the available
      // field names
      const { contentKey, field } = FIELD_LEVEL_ERRORS[code];
      const payload = {
        displayError: true,
        error: getContent(contentKey),
        errorCode: code,
        isValid: false,
      };

      actions.form.updateNonAddressField({
        field,
        payload,
      });
    });
    return focusOnFirstError(appStore.getState().form);
  }
};

const isNameValidationError = (error: errorType): boolean | undefined => {
  if (error?.errorData?.length) {
    const namesFields =
      /user.first_name|user.last_name|user.middle_name|FIRST_NAME_KANA|LAST_NAME_KANA/;
    const filteredErrors = error.errorData.filter((f: any) => f);
    const [firstField = {}] = filteredErrors;
    return namesFields.test(firstField.fields);
  }
};

export {
  fallbackToHermesOrXoon,
  handle3DSError,
  handleDefaultError,
  handleNamesValidationError,
  handlePageLevelError,
  handlePostAccountCreationError,
  isNameValidationError,
};
